import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogAPI } from '../services/api';
import { Clock, User, ArrowLeft, Loader2, Calendar, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPost = async () => {
      try {
        const response = await blogAPI.getBySlug(slug);
        if (isMounted) {
          setPost(response.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching blog post:', error);
          setError('Post not found');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPost();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-green" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-charcoal mb-4 font-heading">Post Not Found</h1>
          <p className="text-stone-grey mb-6 font-body">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-muted-green hover:bg-muted-green/90 text-white font-body">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <div className="min-h-screen bg-warm-white">
      {/* Hero Image */}
      <div className="relative h-[400px] md:h-[500px] bg-charcoal">
        <img 
          src={post.image || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200'} 
          alt={post.title}
          className="w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/40 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-6 left-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/blog')}
            className="bg-white/90 hover:bg-white text-charcoal"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="container mx-auto max-w-4xl">
            {post.category && (
              <Badge className="bg-muted-green text-white font-body mb-4">
                {post.category}
              </Badge>
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 font-body">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{post.author?.name || 'BOXVERRA'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{post.read_time || '5 min read'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{new Date(post.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="container mx-auto max-w-4xl px-4 py-12">
        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-xl text-stone-grey font-body leading-relaxed mb-8 pb-8 border-b border-soft-sand">
            {post.excerpt}
          </p>
        )}

        {/* Main Content */}
        <div className="prose prose-lg max-w-none font-body text-charcoal">
          {post.content.split('\n\n').map((paragraph, index) => {
            // Check for headings
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              return (
                <h2 key={index} className="text-2xl font-bold text-charcoal mt-8 mb-4 font-heading">
                  {paragraph.replace(/\*\*/g, '')}
                </h2>
              );
            }
            // Check for list items
            if (paragraph.startsWith('- ')) {
              const items = paragraph.split('\n').filter(line => line.startsWith('- '));
              return (
                <ul key={index} className="list-disc pl-6 space-y-2 my-6">
                  {items.map((item, i) => (
                    <li key={i} className="text-stone-grey">{item.replace('- ', '')}</li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={index} className="text-stone-grey leading-relaxed mb-6">
                {paragraph}
              </p>
            );
          })}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-soft-sand">
            <h3 className="text-sm font-semibold text-charcoal mb-4 font-body uppercase tracking-wide">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-soft-sand text-stone-grey rounded-full text-sm font-body"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Share */}
        <div className="mt-8 pt-8 border-t border-soft-sand">
          <h3 className="text-sm font-semibold text-charcoal mb-4 font-body uppercase tracking-wide">Share this article</h3>
          <div className="flex gap-3">
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a 
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-sky-500 hover:bg-sky-600 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <Twitter className="h-5 w-5" />
            </a>
            <a 
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-blue-700 hover:bg-blue-800 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Author Bio */}
        {post.author && (
          <div className="mt-12 p-6 bg-soft-sand rounded-xl">
            <div className="flex items-start gap-4">
              {post.author.avatar && (
                <img 
                  src={post.author.avatar} 
                  alt={post.author.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              )}
              <div>
                <h3 className="font-semibold text-charcoal font-heading">{post.author.name}</h3>
                {post.author.bio && (
                  <p className="text-stone-grey font-body mt-1">{post.author.bio}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 bg-soft-sand rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-3 font-heading text-charcoal">Ready to Create Your Sanctuary?</h3>
          <p className="mb-6 text-stone-grey font-body">Explore our curated collection of wellness products.</p>
          <Link to="/shop">
            <Button className="bg-muted-green text-white hover:bg-muted-green/90 font-body">
              Shop Now
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
